import classes from "./Drivers.module.css";
import WhitePlusIcon from "../../images/deliveries/whitePlusIcon.svg";
import { RouteIcon } from "../../images/drivers/routeIcon";
import { ActionBtn } from "../../components/buttons/actionBtn/ActionBtn";
import { BaseTable } from "../../components/UI/baseTable/BaseTable";
import { useNavigate } from "react-router-dom";
import { PageWrapper } from "../../components/UI/pageWrapper/PageWrapper";
import { fetchDrivers } from "../../redux/driver/thunks/getDriverThunk";
import { LocationIcon } from "../../images/drivers/locationIcon";
import { ThreeDotsIcon } from "../../images/threeDotsIcon";
import { ResetFiltersBtn } from "../../components/buttons/resetFiltersBtn/ResetFiltersBtn";
import { useTranslations } from "../../helperFunctions/customHooks/useTranslations";
import { PackageListIcon } from "../../images/packageList";
import { fetchDeliveries } from "../../redux/package/thunks/getDeliveryThunk";
import { resetDeliveryState } from "../../redux/package/slices/getDeliverySlice";
import { useEffect, useState } from "react";
import { fetchCurrentDeliveries } from "../../redux/package/thunks/getCurrentDeliveriesThunk";
import { useDispatch, useSelector } from "react-redux";
import { resetCurrentDeliveriesState } from "../../redux/package/slices/getCurrentDeliveriesSlice";
import {
  toggleAddNewDriverModal,
  setIsEditMode,
  setIsMapShown,
  setIsModalsBgShown,
  setSelectedDriverInfo,
  setSelectedDriverId,
  toggleDriverLocationModal,
  setShowThreeDotsModal,
  setIsDriverRoutes,
  setIsDriverLocationFromRoutes,
  toggleDriverInfoModal,
  setIsWithHistory,
  toggleThreeDotsModal,
  setSelectedDataForEdit,
} from "../../redux/globalFunctions/globalFnSlice";

export function Drivers() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { status, drivers, total_count, filtered_count } = useSelector(
    (state) => state.drivers
  );
  const searchQuery = useSelector((state) => state.search.query);
  const updateDriverState = useSelector((state) => state.updateDriver);
  const inviteDriverState = useSelector((state) => state.inviteDriver);
  const deleteDriverState = useSelector((state) => state.deleteDriver);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [isFiltered, setIsFiltered] = useState(false);
  const [sortState, setSortState] = useState({ field: "", order: "ASC" });
  const { t } = useTranslations();
  const driversPage = t.drivers;

  // Main fetching effect
  useEffect(() => {
    const filters = {};
    if (searchQuery) {
      filters.query = searchQuery;
      setCurrentPage(1);
      setIsFiltered(true);
    }

    dispatch(
      fetchDrivers({
        filters,
        sort_by: sortState.field,
        sort_order: sortState.order,
        page: currentPage,
        per_page: itemsPerPage,
      })
    );
  }, [dispatch, currentPage, itemsPerPage, sortState, searchQuery]);

  // Refetch on successful operations
  useEffect(() => {
    if (
      updateDriverState.status === "success" ||
      inviteDriverState.status === "success" ||
      deleteDriverState.status === "success"
    ) {
      dispatch(
        fetchDrivers({
          filters: { query: searchQuery },
          sort_by: sortState.field,
          sort_order: sortState.order,
          page: currentPage,
          per_page: itemsPerPage,
        })
      );
    }
  }, [
    inviteDriverState.status,
    dispatch,
    updateDriverState.status,
    deleteDriverState.status,
    searchQuery,
    sortState,
    currentPage,
    itemsPerPage,
  ]);

  const handleSort = (field) => {
    setSortState((prevState) => {
      if (prevState.field === field) {
        return {
          ...prevState,
          order: prevState.order === "ASC" ? "DESC" : "ASC",
        };
      } else {
        return { field, order: "ASC" };
      }
    });
  };

  const totalPages = Math.ceil((total_count || 0) / 10);
  const totalFilteredPages = Math.ceil((filtered_count || 0) / 10);

  const columns = [
    { label: driversPage.table.id },
    { label: driversPage.table.name },
    { label: driversPage.table.email },
    { label: driversPage.table.phone },
    { label: driversPage.table.vehicle },
    { label: driversPage.table.packageList },
    { label: driversPage.table.location },
    { label: driversPage.table.routes },
    { label: driversPage.table.action },
  ];

  function handleAddNewDriverClick() {
    dispatch(toggleAddNewDriverModal());
    dispatch(setIsEditMode(false));
  }

  function handleOpenMapClick() {
    dispatch(setIsMapShown(true));
    dispatch(setIsModalsBgShown(true));
    dispatch(fetchDeliveries({}));
  }

  function handleResetFilters() {
    setSortState({ field: "", order: "ASC" });
  }

  const headerButtons = [
    <ActionBtn
      onClick={handleOpenMapClick}
      text={driversPage.mapBtn}
      height={"42px"}
    />,
    <ActionBtn
      onClick={handleAddNewDriverClick}
      text={driversPage.btnText}
      imgSrc={WhitePlusIcon}
      height={"42px"}
    />,
  ];

  function handlePackageListClick(id, data) {
    navigate(`/package-list/${id}`);
    dispatch(setSelectedDriverInfo(data));
  }

  function handleLocationClick(id, data) {
    dispatch(resetCurrentDeliveriesState());
    dispatch(resetDeliveryState());
    dispatch(fetchCurrentDeliveries({ driver_id: id }));
    dispatch(setSelectedDriverId(id));
    dispatch(toggleDriverLocationModal());
    dispatch(setShowThreeDotsModal(false));
    dispatch(setIsDriverRoutes(false));
    dispatch(setIsDriverLocationFromRoutes(false));
    dispatch(setSelectedDataForEdit(data));
  }

  function handleRouteClick(id) {
    dispatch(fetchCurrentDeliveries({ driver_id: id }));
    dispatch(setSelectedDriverId(id));
    dispatch(toggleDriverInfoModal());
    dispatch(setIsDriverRoutes(true));
    dispatch(setShowThreeDotsModal(false));
  }

  function handleThreeDotsClick(e, id, data) {
    const buttonPosition = e.currentTarget.getBoundingClientRect();
    const scrollY = window.scrollY || window.pageYOffset;

    const payload = {
      x: buttonPosition.left + window.scrollX - 55,
      y: buttonPosition.top + scrollY - 60,
    };
    dispatch(toggleThreeDotsModal(payload));
    dispatch(setIsEditMode(true));
    dispatch(setIsWithHistory(true));
    dispatch(setSelectedDriverId(id));
    dispatch(setSelectedDataForEdit(data));
  }

  return (
    <PageWrapper title={driversPage.drivers} buttons={headerButtons}>
      <BaseTable
        status={status}
        errorMsg={driversPage.errorFetching}
        noItemsMsg={driversPage.noDriversFound}
        items={drivers}
        resultsContainerHeight={"calc(100vh - 370px)"}
        columns={columns}
        columnAndRowWidth={"210px"}
        pagination={true}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalPages={totalPages}
        totalFilteredPages={totalFilteredPages}
        isFiltered={isFiltered}
        filters={
          <>
            {driversPage.filters.filterBy}
            <button
              onClick={() => handleSort("u_firstname")}
              className={`${classes.sortBtn} ${
                sortState.field === "u_firstname" ? `${classes.activeBtn}` : ""
              }`}
              disabled={drivers.length === 0}
            >
              {driversPage.filters.namesSelect +
                (sortState.field === "u_firstname"
                  ? ` ${sortState.order}`
                  : "")}
            </button>
            <button
              onClick={() => handleSort("u_vehicle_id")}
              className={`${classes.sortBtn} ${
                sortState.field === "u_vehicle_id" ? `${classes.activeBtn}` : ""
              }`}
              disabled={drivers.length === 0}
            >
              {driversPage.filters.vehiclesSelect +
                (sortState.field === "u_vehicle_id"
                  ? ` ${sortState.order}`
                  : "")}
            </button>

            <ResetFiltersBtn
              disabled={sortState.field === "" && sortState.order === "ASC"}
              onClick={handleResetFilters}
              text={driversPage.filters.resetSelectsBtn}
            />
          </>
        }
        rows={
          drivers &&
          drivers?.map((item) => [
            item.u_id,
            item.u_firstname + " " + item.u_lastname,
            item.u_email,
            item.u_phonenumber,
            item.vehicle_name,
            <PackageListIcon
              onClick={() => handlePackageListClick(item.u_id, item)}
            />,
            <LocationIcon
              onClick={() => handleLocationClick(item.u_id, item)}
            />,
            <RouteIcon onClick={() => handleRouteClick(item.u_id)} />,
            <ThreeDotsIcon
              onClick={(e) => handleThreeDotsClick(e, item.u_id, item)}
            />,
          ])
        }
      />
    </PageWrapper>
  );
}
